import React, { Component } from 'react';
import { Form, Input, Checkbox, Spin, Select } from 'antd';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import pickBy from 'lodash/pickBy';
import identity from 'lodash/identity';
import styled from '@emotion/styled/macro';
import { ArrowLeftOutlined, ArrowRightOutlined } from '@ant-design/icons';

import {
  Container,
  formLayout,
  formValidateMessages,
  AddSaveHint,
  ModalV2,
  PrimaryButtonV2,
} from 'utils';

import { ProjectsActions } from 'redux/actions';
import { LoginFormV2, RegisteredForm } from 'components';

const FormItem = Form.Item;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;

  @media (max-width: 520px) {
    width: 100%;
    max-width: 100%;
  }
`;

const Heading = styled.div`
  margin: 0;
  width: 100%;
  font-family: Inter;
  font-weight: 600;
  font-size: 30px;
  line-height: 38px;
  color: #181d27;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const SectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const SectionTitle = styled.div`
  margin: 0;
  font-family: Inter;
  font-weight: 600;
  font-size: 20px;
  line-height: 30px;
  color: #181d27;
`;

const SectionText = styled.div`
  margin: 0;
  font-family: Inter;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: #535862;
`;

const StyledForm = styled(Form)`
  width: 100%;

  .ant-form-item {
    margin-bottom: 0;
    width: 100%;
  }

  .ant-form-item-label {
    padding-bottom: 6px;
  }

  .ant-form-item-label > label {
    height: 20px;
    font-family: Inter;
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    color: #414651;
  }

  .ant-form-item-label > label::after {
    display: none;
  }

  .ant-form-item-required::before {
    display: none !important;
  }

  .ant-form-item-required::after {
    display: inline-block !important;
    content: '*';
    margin-left: 2px;
    color: #9dbc60;
    font-size: 14px;
    line-height: 20px;
  }

  .ant-form-item-extra {
    margin-top: 6px;
    font-family: Inter;
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    color: #535862;
  }
`;

const CurrencyField = styled.div`
  width: 100%;

  .currency-input-row {
    display: flex;
    width: 100%;
    height: 40px;
    background: #fff;
    border: 1px solid #d5d7da;
    box-shadow: 0px 1px 2px rgba(10, 13, 18, 0.05);
    border-radius: 8px;
    overflow: hidden;
  }

  .currency-addon {
    display: flex;
    align-items: center;
    padding: 8px 12px;
    width: 56px;
    font-family: Inter;
    font-size: 16px;
    line-height: 24px;
    color: #535862;
    background: #fff;
  }

  input {
    width: 100%;
    height: 40px;
    padding: 8px 12px;
    border: 0 !important;
    border-left: 1px solid #d5d7da !important;
    box-shadow: none !important;
    border-radius: 0 !important;
    font-family: Inter;
    font-size: 16px;
    line-height: 24px;
    color: #181d27;
  }

  input::placeholder {
    color: #717680;
  }

  input:focus {
    box-shadow: none !important;
  }
`;

const CheckboxBlock = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;

  .ant-checkbox-wrapper {
    align-items: flex-start;
    margin: 0;
  }

  .ant-checkbox {
    top: 2px;
  }

  .ant-checkbox-inner {
    width: 20px;
    height: 20px;
    border-radius: 6px;
    border-color: #d5d7da;
  }

  .ant-checkbox-checked .ant-checkbox-inner {
    background-color: #9dbc60;
    border-color: #9dbc60;
  }

  .ant-checkbox + span {
    padding-inline-start: 12px;
    padding-inline-end: 0;
  }
`;

const CheckboxTextWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const CheckboxTitle = styled.div`
  font-family: Inter;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  color: #414651;
`;

const CheckboxDescription = styled.div`
  font-family: Inter;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #535862;
`;

const StyledSelect = styled(Select)`
  width: 100%;
  margin-top: 6px;

  .ant-select-selector {
    height: 40px !important;
    padding: 4px 12px !important;
    border: 1px solid #d5d7da !important;
    box-shadow: 0px 1px 2px rgba(10, 13, 18, 0.05) !important;
    border-radius: 8px !important;
  }

  .ant-select-selection-item {
    font-family: Inter;
    font-weight: 500;
    font-size: 16px;
    line-height: 30px !important;
    color: #181d27;
  }
`;

const Actions = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  align-content: center;
  justify-content: center;
  gap: 6px;
  height: 44px;
  padding: 10px 16px;
  border: 0;
  background: transparent;
  border-radius: 8px;
  font-family: Inter;
  font-weight: 600;
  font-size: 16px;
  color: #535862;
  cursor: pointer;

  p {
    padding:0 !important;
    margin: 0;
  }
`;

const NextButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 44px;
  padding: 10px 16px;
  border: 0;
  background: #9dbc60;
  color: #fff;
  border-radius: 8px;
  box-shadow: 0px 1px 2px rgba(10, 13, 18, 0.05),
    inset 0px 0px 0px 1px rgba(10, 13, 18, 0.18),
    inset 0px -2px 0px rgba(10, 13, 18, 0.05);
  font-family: Inter;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;

  p {
    margin: 0;
  }
`;

const CurrencyInput = (props) => (
  <CurrencyField>
    <div className="currency-input-row">
      <div className="currency-addon">EUR</div>
      <Input {...props} />
    </div>
  </CurrencyField>
);

class FundingGoal extends Component {
  static propTypes = {
    updateDraft: PropTypes.func.isRequired,
    project: PropTypes.object,
    loading: PropTypes.bool.isRequired,
    errors: PropTypes.object.isRequired,
    projectId: PropTypes.string,
    currentStep: PropTypes.number.isRequired,
    setCanMove: PropTypes.func.isRequired,
    handleExitEditForm: PropTypes.func.isRequired,
    editProject: PropTypes.bool.isRequired,
  };

  state = {
    showLoginModal: false,

    showRegisteredModal: false,

    showDynamicGoal: false,

    currentGoal: null,

    currentFinalGoal: null,

    donationReceiptPossible: false,

    allowOverfunding: false,

    overfundingLimit: 'unlimited',
  };

  componentDidMount() {
    const { project, projectId, updateDraft } = this.props;

    if (projectId) updateDraft(projectId, {});

    const savedData = sessionStorage.getItem('fundingData');
    const parsedData = savedData ? JSON.parse(savedData) : {};

    if (!project) {
      this.setState({
        currentFinalGoal: parsedData.final_goal,
        showDynamicGoal: !!parsedData.enable_dynamic_goals,
        currentGoal: parsedData.goal,
        donationReceiptPossible: !!parsedData.donation_receipts_possible,
      });
      return;
    }

    if (project?.fundingTarget) {
      this.setState({
        currentFinalGoal: project.fundingTarget.final_goal,
        showDynamicGoal: !!project.fundingTarget.goal,
        currentGoal: project.fundingTarget.goal,
        donationReceiptPossible: !!project.donation_receipts_possible,
      });
    }
  }

  handelCloseLoginModal = () => {
    this.setState({ showLoginModal: false });
  };

  handelCloseRegisteredModal = () => {
    this.setState({ showRegisteredModal: false });
  };

  handleDynamicGoal = (e) => {
    this.setState({ showDynamicGoal: e.target.checked });
  };

  handleAllowOverfunding = (e) => {
    this.setState({ allowOverfunding: e.target.checked});
  };

  onFinish = (values) => {
    const { updateDraft, projectId, currentStep, setCanMove, editProject } =
      this.props;

    const client = localStorage.getItem('client');
    const uid = localStorage.getItem('uid');
    const access = localStorage.getItem('access-token');
    const user = localStorage.getItem('user');

    const isLoggedIn = !!(user && !isEmpty(user) && access && uid && client);

    if (!values.enable_dynamic_goals) {
      values.goal = null;
    }

    if (isLoggedIn) {
      if (projectId) {
        updateDraft(projectId, values, currentStep);
        sessionStorage.removeItem('fundingData');
      } else {
        sessionStorage.setItem('fundingData', JSON.stringify(values));
      }

      setCanMove(true);

      if (editProject) this.props.handleSteps(5);
      else this.props.handleSteps(4);
    } else {
      this.setState({
        showLoginModal: true,
        onLoginSuccess: () => {
          sessionStorage.setItem('fundingData', JSON.stringify(values));
          this.props.handleSteps(4);
        },
        onRegisterSuccess: () => {
          this.setState({
            showLoginModal: false,
            showRegisteredModal: true,
          });
        },
      });
    }
  };

  onReturn = () => {
    if (this.props.editProject) this.props.handleExitEditForm();
    else this.props.handleSteps(2);
  };

  render() {
    const { project, loading, setCanMove, projectId, editProject } = this.props;

    const savedData = sessionStorage.getItem('fundingData');
    const parsedData = savedData ? JSON.parse(savedData) : {};

    const client = localStorage.getItem('client');
    const uid = localStorage.getItem('uid');
    const access = localStorage.getItem('access-token');
    const user = localStorage.getItem('user');

    const isLoggedIn = !!(user && !isEmpty(user) && access && uid && client);

    const initialValues = projectId
      ? pickBy(project?.fundingTarget || {}, identity)
      : parsedData || {};

    initialValues.allow_overfunding = this.state.allowOverfunding;

    initialValues.overfunding_limit =
      this.state.overfundingLimit ||
      initialValues.overfunding_limit ||
      'unlimited';

    initialValues.enable_dynamic_goals = this.state.showDynamicGoal;
    initialValues.final_goal =
      this.state.currentFinalGoal || initialValues.final_goal;
    initialValues.goal = this.state.currentGoal || initialValues.goal;
    initialValues.donation_receipts_possible =
      this.state.donationReceiptPossible ||
      initialValues.donation_receipts_possible;

    return (
      <Container >
        <Spin spinning={loading}>
          {!loading && (
            <Content>
              <Heading>Lass uns Ziele setzen.</Heading>

              <StyledForm
                {...formLayout}
                name="project-details"
                onFinish={this.onFinish}
                validateMessages={formValidateMessages}
                onChange={() => setCanMove(false)}
                initialValues={initialValues}
              >
                <Content>
                  <Section>
                    <SectionHeader>
                      <SectionTitle>Wieviel möchtest du sammeln?</SectionTitle>
                    </SectionHeader>

                    <FormItem
                      name="final_goal"
                      label="Gesamtziel"
                      required={false}
                      extra="Mindestens 100,00 €"
                      rules={[
                        {
                          required: true,
                          message: 'Gesamtziel ist erforderlich',
                        },
                      ]}
                    >
                      <CurrencyInput
                        type="number"
                        min={100}
                        placeholder="Betrag eingeben"
                        step=".01"
                      />
                    </FormItem>
                  </Section>

                  <Section>
                    <SectionHeader>
                      <SectionTitle>Dynamisches Etappenziel</SectionTitle>
                      <SectionText>
                        Dein Zielbetrag wird automatisch erhöht, sobald
                        Unterstützungen eingehen, sodass du immer ein aktuelles
                        Zwischenziel hast. So bleibt dein Projekt immer in
                        Bewegung und motiviert deine Unterstützer.
                      </SectionText>
                    </SectionHeader>

                    <div style={{ width: '100%' }}>
                      <CheckboxBlock>
                        <FormItem
                          name="enable_dynamic_goals"
                          valuePropName="checked"
                        >
                          <Checkbox
                            checked={this.state.showDynamicGoal}
                            onChange={this.handleDynamicGoal}
                          >
                            <CheckboxTextWrap>
                              <CheckboxTitle>
                                Ich möchte mein erstes Etappenziel selbst
                                bestimmen.
                              </CheckboxTitle>
                              <CheckboxDescription>
                                Lege ein erstes realistisches Zwischenziel fest.
                                Danach kann dein Ziel dynamisch mitwachsen.
                              </CheckboxDescription>
                            </CheckboxTextWrap>
                          </Checkbox>
                        </FormItem>
                      </CheckboxBlock>

                      {this.state.showDynamicGoal && (
                        <FormItem
                          name="goal"
                          style={{ marginTop: 12 }}
                          rules={[
                            ({ getFieldValue }) => ({
                              validator(_, value) {
                                if (
                                  value &&
                                  Number(value) >
                                    Number(getFieldValue('final_goal'))
                                ) {
                                  return Promise.reject(
                                    'Das erste Etappenziel muss kleiner als das Gesamtziel sein.'
                                  );
                                }

                                return Promise.resolve();
                              },
                            }),
                          ]}
                        >
                          <CurrencyInput
                            type="number"
                            min={100}
                            placeholder="Erstes Etappenziel eingeben"
                            step=".01"
                          />
                        </FormItem>
                      )}
                    </div>
                  </Section>

                  <Section>


                    <div style={{ width: '100%' }}>
                      <CheckboxBlock>
                        <FormItem
                          name="allow_overfunding"
                          valuePropName="checked"
                        >
                          <Checkbox
                            checked={this.state.allowOverfunding}
                            onChange={this.handleAllowOverfunding}
                          >
                            <CheckboxTextWrap>
                              <CheckboxTitle>
                                Über Gesamtziel hinaus sammeln erlauben
                              </CheckboxTitle>
                              <CheckboxDescription>
                                Erlaube weitere Unterstützungen, auch nachdem
                                dein ursprüngliches Ziel erreicht wurde. So kann
                                dein Projekt darüber hinaus wachsen.
                              </CheckboxDescription>
                            </CheckboxTextWrap>
                          </Checkbox>
                        </FormItem>
                      </CheckboxBlock>

                      {this.state.allowOverfunding && (
                        <FormItem
                          name="overfunding_limit"
                          label="Maximale zusätzliche Zielsumme"
                          style={{ marginTop: 8 }}
                        >
                          <StyledSelect
                            placeholder="Bitte auswählen"
                            onChange={(value) =>
                              this.setState({ overfundingLimit: value })
                            }
                          >
                            <Select.Option value="unlimited">
                              Unbegrenzt
                            </Select.Option>

                            <Select.Option value="10">+10 %</Select.Option>

                            <Select.Option value="25">+25 %</Select.Option>
                          </StyledSelect>
                        </FormItem>
                      )}
                    </div>
                  </Section>

                  {editProject && <AddSaveHint />}

                  <Actions>
                    <BackButton
                      primary={false}
                      type="button"
                      onClick={this.onReturn}
                    >
                      {!editProject && <ArrowLeftOutlined />}
                      <p>
                        {editProject ? 'Zurück zur Projektübersicht' : 'Zurück'}
                      </p>
                    </BackButton>

                    <PrimaryButtonV2 type="submit" id="submit-button">
                      <p>{editProject ? 'Speichern*' : 'Weiter'}</p>
                      {!editProject && <ArrowRightOutlined />}
                    </PrimaryButtonV2>
                  </Actions>
                </Content>
              </StyledForm>
            </Content>
          )}
        </Spin>

        <ModalV2
          showModal={isLoggedIn ? false : this.state.showLoginModal}
          handelCloseModal={this.handelCloseLoginModal}
        >
          {this.state.showLoginModal && (
            <LoginFormV2
              onLoginSuccess={this.state.onLoginSuccess}
              onRegisterSuccess={this.state.onRegisterSuccess}
            />
          )}
        </ModalV2>

        <ModalV2
          showModal={this.state.showRegisteredModal}
          handelCloseModal={this.handelCloseRegisteredModal}
        >
          {this.state.showRegisteredModal && <RegisteredForm />}
        </ModalV2>
      </Container>
    );
  }
}

const mapStateToProps = (state) => {
  const { project, loading, errors, currentStep } = state.projects;

  return {
    project,
    loading,
    errors,
    currentStep,
  };
};

const mapDispatchToProps = (dispatch) => ({
  handleSteps: (step) => dispatch(ProjectsActions.handleSteps(step)),
});

export default connect(mapStateToProps, mapDispatchToProps)(FundingGoal);
